import React, { useState, useEffect, ChangeEvent } from "react";
import {
    Dialog,
    Button,
    Input,
    Select,
    MenuItem,
    FormControl,
    CircularProgress,
    Snackbar,
    Alert,
    SelectChangeEvent
} from "@mui/material";
import {
    AddProductImage,
    GetProductsWithImages,
    UpdateProductImage,
    UpdateProductInfo,
} from "../services/ApiServices/ProductService";
import { GetAllSizes } from "../services/ApiServices/SizeService";
import { GetProductSize, UpdateProductSize } from "../services/ApiServices/ProductSize";
import { DialogBody, DialogFooter, DialogHeader, Typography } from "@material-tailwind/react";
import moment from "moment";
import { Materials } from "../services/Material";

type AdminEditProductProps = {
    open: boolean;
    handleOpen: () => void;
    fetchProducts: () => void;
    productId: string | null;
    setLoading: (loading: boolean) => void;
};

function AdminEditProduct({ open, handleOpen, fetchProducts, productId, setLoading }: AdminEditProductProps) {
    const [product, setProduct] = useState<Product | null>(null);
    const [productSize, setProductSize] = useState<ProductSize[]>([]);
    const [sizes, setSizes] = useState<Size[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [formState, setFormState] = useState({
        name: '',
        description: '',
        price: '',
        type: '',
        material: '',
        quantity: '',
        status: '',
        createDate: '',
        images: [] as File[],
    });
    const [loading, setLoadingState] = useState<boolean>(false);

    useEffect(() => {
        if (productId) {
            setLoading(true);
            setLoadingState(true);
            Promise.all([fetchProductData(productId), fetchSizes()])
                .then(() => setLoadingState(false))
                .catch((error) => {
                    console.error("Error fetching data:", error);
                    setErrorMessage("Failed to load product details");
                    setLoadingState(false);
                });
        }
    }, [productId]);

    const fetchProductData = async (productId: string) => {
        try {
            const [productData, sizeData] = await Promise.all([
                GetProductsWithImages(productId),
                GetProductSize(productId),
            ]);
            setProduct(productData);
            setProductSize(sizeData);
            setFormState({
                name: productData.name,
                description: productData.description,
                price: productData.price.toString(),
                type: productData.type,
                material: productData.material,
                quantity: productData.quantity.toString(),
                createDate: productData.createDate,
                status: productData.status,
                images: [],
            });
        } catch (error) {
            console.error("Error fetching product data:", error);
            setErrorMessage("Failed to fetch product data");
        }
    };

    const fetchSizes = async () => {
        try {
            const sizes = await GetAllSizes();
            setSizes(sizes);
        } catch (error) {
            console.error("Error fetching sizes:", error);
            setErrorMessage("Failed to fetch sizes");
        }
    };

    const handleUpdateProduct = async () => {
        if (product && productSize) {
            setLoadingState(true);
            try {
                await UpdateProductInfo({
                    id: product.id,
                    ...formState,
                    quantity: parseInt(formState.quantity),
                    price: parseFloat(formState.price),
                    createDate: new Date(),
                });
                
                await UpdateProductSize(
                    productSize.map(ps => ({ productSizeId: ps.id, quantity: ps.quantity }))
                );

                if (formState.images.length > 0) {
                    const formData = new FormData();
                    formState.images.forEach((file) => {
                        formData.append("images", file);
                    });
                    formData.append("productId", product.id);
                    await UpdateProductImage(formData);
                }

                fetchProducts();
                handleOpenDialog(false);
            } catch (error) {
                console.error("Error updating product:", error);
                setErrorMessage("Failed to update product");
            } finally {
                setLoadingState(false);
            }
        }
    };

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files) {
            setFormState(prevState => ({
                ...prevState,
                images: Array.from(files)
            }));
        }
    };

    const handleSelectChange = (e: SelectChangeEvent<string>) => {
        const { name, value } = e.target;
        setFormState((prev) => ({ ...prev, [name]: value }));
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | { name?: string; value: unknown }>) => {
        const { name, value } = e.target;
        setFormState((prev) => ({ ...prev, [name as string]: value as string }));
    };

    const handleProductSizeChange = (id: string, value: string) => {
        setProductSize((prevState) =>
            prevState.map(ps => (ps.id === id ? { ...ps, quantity: parseInt(value) } : ps))
        );
    };

    const handleOpenDialog = (isOpen: boolean) => {
        if (!isOpen) {
            setProduct(null);
            setProductSize([]);
            setLoading(false)
        }
        handleOpen();
    };

    return (
        <Dialog open={open} onClose={() => handleOpenDialog(false)} maxWidth="sm" fullWidth>
            <DialogHeader placeholder={""}>Cập nhật chi tiết sản phẩm</DialogHeader>
            <DialogBody placeholder={""}>
                <FormControl fullWidth margin="dense">
                    <Typography placeholder={""}>Ngày tạo</Typography>
                    <Input
                        disabled
                        type="date"
                        value={moment(formState.createDate).format("YYYY-MM-DD")}
                        name="createDate"
                    />
                </FormControl>

                <FormControl fullWidth margin="dense">
                    <Typography placeholder={""}>Tên sản phẩm</Typography>
                    <Input
                        type="text"
                        value={formState.name}
                        name="name"
                        onChange={handleChange}
                    />
                </FormControl>

                <FormControl fullWidth margin="dense">
                    <Typography placeholder={""}>Loại sản phẩm</Typography>
                    <Select value={formState.type} name="type" onChange={handleSelectChange}>
                        <MenuItem value="Áo dài">Áo dài</MenuItem>
                        <MenuItem value="Phụ kiện">Phụ kiện</MenuItem>
                    </Select>
                </FormControl>

                <FormControl fullWidth margin="dense">
                    <Typography placeholder={""}>Mô tả</Typography>
                    <Input
                        type="text"
                        value={formState.description}
                        name="description"
                        onChange={handleChange}
                    />
                </FormControl>

                <FormControl fullWidth margin="dense">
                    <Typography placeholder={""}>Giá tiền</Typography>
                    <Input
                        type="number"
                        value={formState.price}
                        name="price"
                        onChange={handleChange}
                    />
                </FormControl>

                <FormControl fullWidth margin="dense">
                    <Typography placeholder={""}>Chất liệu</Typography>
                    <Select value={formState.material} name="material" onChange={handleSelectChange}>
                        {Materials.map((material) => (
                            <MenuItem key={material} value={material}>{material}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth margin="dense">
                    <Typography placeholder={""}>Trạng thái</Typography>
                    <Select value={formState.status} name="status" onChange={handleSelectChange}>
                        <MenuItem value="Active">Active</MenuItem>
                        <MenuItem value="Inactive">Inactive</MenuItem>
                    </Select>
                </FormControl>

                {productSize.map((size) => (
                    <FormControl fullWidth margin="dense" key={size.id}>
                        <Typography placeholder={""}>Số lượng ({size.name})</Typography>
                        <Input
                            type="number"
                            value={size.quantity}
                            onChange={(e) => handleProductSizeChange(size.id, e.target.value)}
                        />
                    </FormControl>
                ))}

                <FormControl fullWidth margin="dense">
                    <Typography placeholder={""}>Hình ảnh sản phẩm</Typography>
                    <Input
                        type="file"
                        inputProps={{ multiple: true }}
                        onChange={handleFileChange}
                    />
                </FormControl>
            </DialogBody>

            <DialogFooter placeholder={""}>
                <Button onClick={() => handleOpenDialog(false)} color="secondary">Huỷ</Button>
                <Button onClick={handleUpdateProduct} color="primary">
                    {loading ? <CircularProgress size={24} /> : "Cập nhật"}
                </Button>
            </DialogFooter>

            <Snackbar open={!!errorMessage} autoHideDuration={6000} onClose={() => setErrorMessage(null)}>
                <Alert onClose={() => setErrorMessage(null)} severity="error">
                    {errorMessage}
                </Alert>
            </Snackbar>
        </Dialog>
    );
}

export default AdminEditProduct;

type Product = {
    id: string;
    name: string;
    description: string;
    type: string;
    material: string;
    price: number;
    quantity: number;
    status: string;
    createDate: string;
};

type ProductSize = {
    id: string;
    name: string;
    quantity: number;
    price: number;
    description: string;
    createdAt: string;
    productId: string;
    sizeId: string;
};

type Size = {
    id: string;
    name: string;
};
